import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateLoanInput, Loan, UpdateLoanInput } from "../../lib/realestate";

interface Props {
  mode: "create" | "edit";
  initial?: Loan;
  onSubmit: (input: CreateLoanInput | UpdateLoanInput) => Promise<void>;
  submitting?: boolean;
  serverError?: string;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function formatEuroDe(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function LoanForm({ mode, initial, onSubmit, submitting, serverError }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "Hauptdarlehen");
  const [principal, setPrincipal] = useState(initial ? formatEuroDe(initial.principalCents) : "");
  const [startDate, setStartDate] = useState(
    initial?.startDate ?? new Date().toISOString().slice(0, 10),
  );
  const [interestPct, setInterestPct] = useState(
    initial ? String(initial.annualInterestRateBp / 100) : "2.85",
  );
  const [payment, setPayment] = useState(initial ? formatEuroDe(initial.monthlyPaymentCents) : "");
  const [localError, setLocalError] = useState<string | null>(null);

  const annuityHint = useMemo(() => {
    try {
      const p = parseEuroDe(principal);
      const r = Math.round(Number.parseFloat(interestPct) * 100);
      const m = parseEuroDe(payment);
      const initialInterest = Math.round((p * r) / 12 / 10000);
      if (m <= initialInterest) {
        return t("realestate.loan.warning_annuity_too_low", {
          interest: formatEuroDe(initialInterest),
        });
      }
      return null;
    } catch {
      return null;
    }
  }, [principal, interestPct, payment, t]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      const principalCents = parseEuroDe(principal);
      const annualInterestRateBp = Math.round(Number.parseFloat(interestPct) * 100);
      const monthlyPaymentCents = parseEuroDe(payment);
      const input: CreateLoanInput = {
        name: name.trim(),
        principalCents,
        startDate,
        annualInterestRateBp,
        monthlyPaymentCents,
      };
      await onSubmit(input);
    } catch {
      setLocalError(t("realestate.errors.invalid_amount"));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.loan.name")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.loan.principal")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            required
            placeholder="200.000,00"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.loan.start_date")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.loan.interest_rate")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            max="30"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={interestPct}
            onChange={(e) => setInterestPct(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.loan.monthly_payment")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
            required
            placeholder="1.000,00"
          />
        </label>
      </div>
      {annuityHint && <div className="text-amber-700 text-sm">{annuityHint}</div>}
      {(localError || serverError) && (
        <div className="text-red-700 text-sm">{localError ?? serverError}</div>
      )}
      <p className="text-xs text-slate-500">{t("realestate.loan.interest_phase_hint")}</p>
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded"
        disabled={submitting}
      >
        {submitting
          ? t("common.submitting")
          : mode === "create"
            ? t("common.create")
            : t("common.save")}
      </button>
    </form>
  );
}
